import React, { useEffect, useRef, useState } from "react";
import { getContractsByID } from "../../api/user";
import { readTrackingPage, searchTracking, startTrackingList } from "../../api/tracking";
import { useTranslation } from "react-i18next";
import { useMessenger } from "../../context/messengerContext";
import BasicDropdown from "../../components/inputs/basicDropdown/basicDropdown";
import BasicPanel from "../../components/panels/basicPanel/basicPanel";
import StepsHeader from "./utils/stepsHeader";
import TrackItem from "./trackItem";
import "./packageTracking.css";

const TrackPackage = () => {
  const { t } = useTranslation('basic');
  const { userId, platform } = useMessenger();
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [groups, setGroups] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const generation = useRef(0);
  const searchGeneration = useRef(0);
  const groupGenerations = useRef({});
  const pending = useRef([]);

  useEffect(() => {
    let active = true;
    getContractsByID({ platform, userId }).then((values) => {
      if (active) setContracts((values || []).map((value) => ({ label: value, value })));
    }).catch(() => { if (active) setError("Не удалось загрузить договоры"); });
    return () => { active = false; };
  }, [platform, userId]);

  useEffect(() => {
    const current = ++generation.current;
    pending.current.forEach((controller) => controller.abort());
    pending.current = [];
    setSnapshot(null);
    setGroups({});
    groupGenerations.current = {};
    setExpanded([]);
    setSearchResult(null);
    setError("");
    if (!selectedContract) return;
    const controller = new AbortController();
    startTrackingList({ platform, userId, contract_number: selectedContract, signal: controller.signal })
      .then((result) => { if (current === generation.current) setSnapshot(result); })
      .catch((failure) => { if (!controller.signal.aborted && current === generation.current) setError(failure.message); });
    return () => { generation.current += 1; controller.abort(); };
  }, [platform, selectedContract, userId, refresh]);

  const loadGroup = async (group, cursor = null) => {
    const current = generation.current;
    const groupId = (groupGenerations.current[group] || 0) + 1;
    groupGenerations.current[group] = groupId;
    const controller = new AbortController();
    pending.current.push(controller);
    setGroups((previous) => ({ ...previous, [group]: {
      ...previous[group], rows: cursor ? previous[group]?.rows || [] : [], loading: true, error: '',
    } }));
    try {
      const result = await readTrackingPage({ platform, userId, contract_number: selectedContract,
        snapshot_id: snapshot.snapshot_id, group, cursor, limit: 20, signal: controller.signal });
      if (current !== generation.current || groupGenerations.current[group] !== groupId || controller.signal.aborted) return;
      setGroups((previous) => ({ ...previous, [group]: { rows: cursor ? [...previous[group].rows, ...result.rows] : result.rows,
                                                      next: result.next_cursor, loading: false } }));
    } catch (failure) {
      if (current === generation.current && groupGenerations.current[group] === groupId && !controller.signal.aborted)
        setGroups((previous) => ({ ...previous, [group]: { ...previous[group], loading: false, error: failure.message } }));
    } finally {
      pending.current = pending.current.filter((value) => value !== controller);
    }
  };

  const toggleGroup = (group) => {
    if (expanded.includes(group)) {
      groupGenerations.current[group] = (groupGenerations.current[group] || 0) + 1;
      setExpanded((previous) => previous.filter((value) => value !== group));
    }
    else { setExpanded((previous) => [...previous, group]); loadGroup(group); }
  };

  const search = async (cursor = null) => {
    const current = generation.current;
    const searchId = ++searchGeneration.current;
    const controller = new AbortController();
    pending.current.push(controller);
    if (!cursor) setSearchResult(null);
    setSearching(true);
    setError('');
    try {
      const result = await searchTracking({ platform, userId, contract_number: selectedContract,
        snapshot_id: snapshot.snapshot_id, criterion: { mode: 'ui', substring: query.trim() }, cursor, limit: 20,
        signal: controller.signal });
      if (current !== generation.current || searchId !== searchGeneration.current) return;
      setSearchResult((previous) => ({ ...result, rows: cursor ? [...(previous?.rows || []), ...result.rows] : result.rows }));
    } catch (failure) {
      if (current === generation.current && searchId === searchGeneration.current && !controller.signal.aborted)
        setError(failure.message);
    } finally {
      pending.current = pending.current.filter((value) => value !== controller);
      if (current === generation.current && searchId === searchGeneration.current) setSearching(false);
    }
  };

  const item = (row) => <TrackItem key={row.track_number} track_data={{ ...row, ...(row.logistics || {}),
    contract_number: selectedContract, status: row.logistics?.status || (row.availability !== 'available' ? 'Данные СДЭК недоступны' : null) }} />;

  return <>
    <StepsHeader />
    <div style={{ padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <BasicPanel><BasicDropdown label={t('contract_number')} data={contracts} value={selectedContract}
                                placeholder={t('select_contract_number')} onChange={setSelectedContract} /></BasicPanel>
      {error && <div role="alert">{error}</div>}
      {selectedContract && !snapshot && !error && <div role="status">Загружаю список…</div>}
      {snapshot && <>
        <div>Отправлений в списке: {snapshot.known_catalog_count}</div>
        <button type="button" onClick={() => setRefresh((value) => value + 1)}>Обновить</button>
        <label>Поиск по номеру, маршруту или статусу<input value={query} onChange={(event) => {
          searchGeneration.current += 1; setQuery(event.target.value); setSearchResult(null); setSearching(false);
        }} /></label>
        <button type="button" disabled={searching || !query.trim()} onClick={() => search()}>Найти</button>
        {searching && <div role="status">Проверяю данные СДЭК…</div>}
        {searchResult?.rows?.map(item)}
        {searchResult?.search_outcome === 'in_progress' && <div>Поиск ещё не завершён</div>}
        {searchResult?.search_outcome === 'blocked' && <div>Поиск не удалось завершить. Повторите просмотр или обратитесь к менеджеру.</div>}
        {searchResult?.search_outcome === 'completed' && !searchResult.page_changed && !searchResult.rows.length && <div>Совпадений не найдено</div>}
        {searchResult?.page_changed && <div>Данные этой страницы изменились. Повторите поиск.</div>}
        {searchResult?.next_cursor && <button type="button" disabled={searching} onClick={() => search(searchResult.next_cursor)}>Продолжить поиск</button>}
        {!query && snapshot.groups.map((metadata) => {
          const group = groups[metadata.group] || {};
          return <section key={metadata.group}>
            <button type="button" onClick={() => toggleGroup(metadata.group)}>{metadata.user_name || t('employee_word')} ({metadata.count})</button>
            {expanded.includes(metadata.group) && <div>
              {group.loading && <div role="status">Проверяю данные СДЭК…</div>}
              {group.error && <div role="alert">{group.error}<button type="button" onClick={() => loadGroup(metadata.group)}>Повторить</button></div>}
              {(group.rows || []).map(item)}
              {group.next && <button type="button" disabled={group.loading} onClick={() => loadGroup(metadata.group, group.next)}>Загрузить ещё</button>}
            </div>}
          </section>;
        })}
      </>}
    </div>
  </>;
};

export default TrackPackage;
